/**
 * User Profile Service
 * 
 * Core service for managing user adaptive profiles:
 * - Profile initialization
 * - Profile updates
 * - Firestore persistence
 * - Profile retrieval
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import type { 
  UserAdaptiveProfile,
  RecoveryProfile,
  ProgressionProfile,
  RPECalibration,
  FitnessModel,
  PerformanceMetrics,
  DataQuality
} from './types/userProfile';

const PROFILE_COLLECTION = 'userAdaptiveProfiles';
const CURRENT_SCHEMA_VERSION = 1;

/**
 * Initialize a new user profile with default values
 */
export function createDefaultProfile(userId: string): UserAdaptiveProfile {
  const now = new Date();
  
  return {
    userId,
    createdAt: now,
    lastUpdated: now,
    version: CURRENT_SCHEMA_VERSION,
    
    recoveryProfile: {
      capacity: 'average',
      speed: 'average',
      consistency: 0.5,
      confidence: 0,
      baselineReadiness: 70,
      optimalRestDays: 1,
      lastUpdated: now,
    },
    
    progressionProfile: {
      responseToVolume: 'moderate',
      responseToIntensity: 'moderate',
      optimalFrequency: 3,
      preferredProgressionRate: 0.025, // 2.5% per session (conservative start)
      confidence: 0,
      lastUpdated: now,
    },
    
    rpeCalibration: {
      tendency: 'accurate',
      calibrationFactor: 1.0,
      confidence: 0,
      sampleSize: 0,
      lastUpdated: now,
    },
    
    fitnessModel: {
      currentFitness: 50,
      currentFatigue: 20,
      readiness: 30,
      fitnessDecayRate: 15,  // 15-day half-life
      fatigueDecayRate: 3,   // 3-day half-life
      lastCalculated: now,
    },
    
    performanceMetrics: {
      totalSessions: 0,
      totalVolume: 0,
      averageSessionRPE: 7,
      deloadFrequency: 0,
      injuryHistory: [],
      personalRecords: [],
    },
    
    dataQuality: {
      sessionsTracked: 0,
      consistencyScore: 0,
      timeInSystem: 0,
      overallConfidence: 'low',
      lastAssessed: now,
    },
    
    learningEnabled: true,
    lastAnalysisDate: now,
    nextScheduledAnalysis: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000), // 1 week
  };
}

/**
 * Get user profile from Firestore
 * Creates default profile if none exists
 */
export async function getUserProfile(userId: string): Promise<UserAdaptiveProfile | null> {
  try {
    // Check if we're in a browser environment and have Firebase initialized
    if (typeof window === 'undefined' || !db) {
      console.log(' Firebase not available in this environment');
      return null;
    }

    const profileRef = doc(db, PROFILE_COLLECTION, userId);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const data = profileSnap.data();
      
      // Convert Firestore Timestamps to Dates
      return convertTimestampsToProfile(data);
    } else {
      console.log('ℹ No profile found for user:', userId);
      return null;
    }
  } catch (error: any) {
    // Don't log permissions errors - they're expected when not authenticated
    if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
      console.log('ℹ User profile not accessible (not authenticated). Using local data only.');
    } else {
      console.error(' Error getting user profile:', error);
    }
    return null;
  }
}

/**
 * Save user profile to Firestore
 */
export async function saveUserProfile(profile: UserAdaptiveProfile): Promise<void> {
  try {
    const profileRef = doc(db, PROFILE_COLLECTION, profile.userId);
    
    // Convert Dates to Firestore Timestamps for storage
    const firestoreData = convertProfileToFirestore(profile);
    
    await setDoc(profileRef, firestoreData, { merge: true });
    console.log('� Saved user profile for', profile.userId);
  } catch (error) {
    console.error(' Error saving user profile:', error);
    throw error;
  }
}

/**
 * Update specific fields in user profile
 */
export async function updateUserProfile(
  userId: string, 
  updates: Partial<UserAdaptiveProfile>
): Promise<void> {
  try {
    const profileRef = doc(db, PROFILE_COLLECTION, userId);
    
    const firestoreUpdates = {
      ...convertProfileToFirestore(updates as UserAdaptiveProfile),
      lastUpdated: serverTimestamp(),
    };
    
    await updateDoc(profileRef, firestoreUpdates);
    console.log('� Updated user profile for', userId);
  } catch (error) {
    console.error(' Error updating user profile:', error);
    throw error;
  }
}

/**
 * Convert Firestore Timestamps to JavaScript Dates
 */
function convertTimestampsToProfile(data: any): UserAdaptiveProfile {
  const convertTimestamp = (ts: any): Date => {
    if (ts instanceof Timestamp) {
      return ts.toDate();
    }
    if (ts?.toDate) {
      return ts.toDate();
    }
    if (typeof ts === 'string') {
      return new Date(ts);
    }
    return new Date();
  };
  
  return {
    ...data,
    createdAt: convertTimestamp(data.createdAt),
    lastUpdated: convertTimestamp(data.lastUpdated),
    recoveryProfile: {
      ...data.recoveryProfile,
      lastUpdated: convertTimestamp(data.recoveryProfile?.lastUpdated),
    },
    progressionProfile: {
      ...data.progressionProfile,
      lastUpdated: convertTimestamp(data.progressionProfile?.lastUpdated),
    },
    rpeCalibration: {
      ...data.rpeCalibration,
      lastUpdated: convertTimestamp(data.rpeCalibration?.lastUpdated),
    },
    fitnessModel: {
      ...data.fitnessModel,
      lastCalculated: convertTimestamp(data.fitnessModel?.lastCalculated),
    },
    performanceMetrics: {
      ...data.performanceMetrics,
      injuryHistory: data.performanceMetrics?.injuryHistory?.map((injury: any) => ({
        ...injury,
        date: convertTimestamp(injury.date),
      })) || [],
      personalRecords: data.performanceMetrics?.personalRecords?.map((pr: any) => ({
        ...pr,
        date: convertTimestamp(pr.date),
      })) || [],
    },
    dataQuality: {
      ...data.dataQuality,
      lastAssessed: convertTimestamp(data.dataQuality?.lastAssessed),
    },
    lastAnalysisDate: convertTimestamp(data.lastAnalysisDate),
    nextScheduledAnalysis: convertTimestamp(data.nextScheduledAnalysis),
  } as UserAdaptiveProfile;
}

/**
 * Convert JavaScript Dates to Firestore Timestamps
 */
function convertProfileToFirestore(profile: UserAdaptiveProfile): any {
  return {
    ...profile,
    createdAt: Timestamp.fromDate(profile.createdAt),
    lastUpdated: Timestamp.fromDate(profile.lastUpdated),
    recoveryProfile: {
      ...profile.recoveryProfile,
      lastUpdated: Timestamp.fromDate(profile.recoveryProfile.lastUpdated),
    },
    progressionProfile: {
      ...profile.progressionProfile,
      lastUpdated: Timestamp.fromDate(profile.progressionProfile.lastUpdated),
    },
    rpeCalibration: {
      ...profile.rpeCalibration,
      lastUpdated: Timestamp.fromDate(profile.rpeCalibration.lastUpdated),
    },
    fitnessModel: {
      ...profile.fitnessModel,
      lastCalculated: Timestamp.fromDate(profile.fitnessModel.lastCalculated),
    },
    performanceMetrics: {
      ...profile.performanceMetrics,
      injuryHistory: profile.performanceMetrics.injuryHistory.map(injury => ({
        ...injury,
        date: Timestamp.fromDate(injury.date),
      })),
      personalRecords: profile.performanceMetrics.personalRecords.map(pr => ({
        ...pr,
        date: Timestamp.fromDate(pr.date),
      })),
    },
    dataQuality: {
      ...profile.dataQuality,
      lastAssessed: Timestamp.fromDate(profile.dataQuality.lastAssessed),
    },
    lastAnalysisDate: Timestamp.fromDate(profile.lastAnalysisDate),
    nextScheduledAnalysis: Timestamp.fromDate(profile.nextScheduledAnalysis),
  };
}

/**
 * Check if profile needs analysis update
 */
export function shouldAnalyzeProfile(profile: UserAdaptiveProfile): boolean {
  const now = new Date();
  return now >= profile.nextScheduledAnalysis;
}

/**
 * Get profile confidence level based on data quality
 */
export function getProfileConfidence(profile: UserAdaptiveProfile): number {
  const weights = {
    recovery: 0.25,
    progression: 0.25,
    rpe: 0.20,
    dataQuality: 0.30,
  };
  
  const weightedConfidence = 
    (profile.recoveryProfile.confidence * weights.recovery) +
    (profile.progressionProfile.confidence * weights.progression) +
    (profile.rpeCalibration.confidence * weights.rpe) +
    (profile.dataQuality.consistencyScore * weights.dataQuality);
  
  return Math.min(1, Math.max(0, weightedConfidence));
}

